import React from 'react';
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import {ArrowRight, Sparkles} from 'lucide-react';
import {brand, brandFont} from '../brand';
import {BgGradient} from '../components/BgGradient';

export type LaunchTeaserProps = {
  badge: string;
  headline: string;
  cta: string;
};

export const LaunchTeaser: React.FC<LaunchTeaserProps> = ({
  badge,
  headline,
  cta,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const badgeScale = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    config: {damping: 11, stiffness: 110},
  });
  const badgeOpacity = interpolate(frame, [0, 12], [0, 1], {
    extrapolateRight: 'clamp',
  });

  const headlineOpacity = interpolate(frame, [15, 32], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const headlineY = interpolate(frame, [15, 32], [40, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const headlinePulse =
    1 + Math.sin((frame - 32) / 22) * 0.012 * (frame > 32 ? 1 : 0);

  const ctaScale = spring({
    frame: Math.max(0, frame - 50),
    fps,
    from: 0.7,
    to: 1,
    config: {damping: 12, stiffness: 130},
  });
  const ctaOpacity = interpolate(frame, [50, 72], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const glowOpacity = interpolate(frame, [30, 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill>
      <BgGradient />

      <AbsoluteFill
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          opacity: glowOpacity,
        }}
      >
        <div
          style={{
            width: 900,
            height: 900,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${brand.primary}33 0%, ${brand.primary}00 65%)`,
            filter: 'blur(40px)',
          }}
        />
      </AbsoluteFill>

      <AbsoluteFill
        style={{
          padding: '0 72px',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 56,
        }}
      >
        <Badge label={badge} scale={badgeScale} opacity={badgeOpacity} />

        <div
          style={{
            fontFamily: brandFont,
            fontSize: 128,
            fontWeight: 900,
            color: brand.textPrimary,
            letterSpacing: -4,
            lineHeight: 0.98,
            textAlign: 'center',
            whiteSpace: 'pre-line',
            opacity: headlineOpacity,
            transform: `translateY(${headlineY}px) scale(${headlinePulse})`,
            textShadow: `0 24px 60px rgba(0,0,0,0.6), 0 0 80px ${brand.primary}33`,
          }}
        >
          {headline}
        </div>

        <CTAButton label={cta} scale={ctaScale} opacity={ctaOpacity} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const Badge: React.FC<{label: string; scale: number; opacity: number}> = ({
  label,
  scale,
  opacity,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '18px 36px',
      borderRadius: 999,
      background: `linear-gradient(135deg, ${brand.action} 0%, ${brand.accent} 100%)`,
      boxShadow: `0 18px 50px ${brand.action}55`,
      transform: `scale(${scale})`,
      opacity,
    }}
  >
    <Sparkles size={32} color={brand.textPrimary} strokeWidth={2.6} />
    <span
      style={{
        fontFamily: brandFont,
        fontSize: 36,
        fontWeight: 900,
        color: brand.textPrimary,
        letterSpacing: 5,
      }}
    >
      {label}
    </span>
  </div>
);

const CTAButton: React.FC<{label: string; scale: number; opacity: number}> = ({
  label,
  scale,
  opacity,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 18,
      padding: '32px 64px',
      borderRadius: 40,
      background: brand.primary,
      boxShadow: `0 22px 60px ${brand.primary}66`,
      transform: `scale(${scale})`,
      opacity,
      fontFamily: brandFont,
      fontSize: 52,
      fontWeight: 800,
      color: brand.textPrimary,
      letterSpacing: -0.5,
    }}
  >
    {label}
    <ArrowRight size={48} color={brand.textPrimary} strokeWidth={3} />
  </div>
);
